import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, IndianRupee, Users, FileEdit } from "lucide-react";
import { motion } from "framer-motion";
import { AdminStats } from "@/services/adminService";
import { cn } from "@/lib/utils";

interface AdminStatsCardsProps {
  stats: AdminStats;
  loading?: boolean;
}

function formatCurrency(amount: number | undefined) {
  if (!amount || amount === 0) return '₹0';
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)} Crore`;
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)} Lakh`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function AdminStatsCards({ stats, loading }: AdminStatsCardsProps) {
  // Ensure all stats have safe values
  const safeStats = {
    totalLoginToday: stats?.totalLoginToday || 0,
    loginAmount: stats?.loginAmount || 0,
    totalDisbursementToday: stats?.totalDisbursementToday || 0,
    disbursedAmount: stats?.disbursedAmount || 0,
    totalNewLeads: stats?.totalNewLeads || 0,
    totalNewVendorsAdded: stats?.totalNewVendorsAdded || 0,
    totalVendorVisits: stats?.totalVendorVisits || 0,
    totalLeadsStatusUpdated: stats?.totalLeadsStatusUpdated || 0,
    totalLeadsInPipeline: stats?.totalLeadsInPipeline || 0,
  };

  // Format each stat card
  const statCards = [
    {
      title: "LOGIN",
      value: safeStats.totalLoginToday,
      subtitle: formatCurrency(safeStats.loginAmount),
      icon: TrendingUp,
      color: "bg-success/10 text-success",
      gradient: "from-green-500/20 to-green-600/20",
      iconBg: "bg-green-100 dark:bg-green-900",
    },
    {
      title: "DISBURSED",
      value: safeStats.totalDisbursementToday,
      subtitle: formatCurrency(safeStats.disbursedAmount),
      icon: IndianRupee,
      color: "bg-primary/10 text-primary",
      gradient: "from-blue-500/20 to-blue-600/20",
      iconBg: "bg-blue-100 dark:bg-blue-900",
    },
    {
      title: "LEADS",
      value: safeStats.totalNewLeads,
      subtitle: 'Added',
      icon: FileEdit,
      color: "bg-cyan/10 text-cyan",
      gradient: "from-cyan-500/20 to-cyan-600/20",
      iconBg: "bg-cyan-100 dark:bg-cyan-900",
    },
    {
      title: "VENDORS",
      value: safeStats.totalNewVendorsAdded,
      subtitle: 'Added',
      icon: Users,
      color: "bg-purple/10 text-purple",
      gradient: "from-purple-500/20 to-purple-600/20",
      iconBg: "bg-purple-100 dark:bg-purple-900",
    },
    {
      title: "VENDORS",
      value: safeStats.totalVendorVisits,
      subtitle: 'Visited',
      icon: Users,
      color: "bg-accent/10 text-accent",
      gradient: "from-indigo-500/20 to-indigo-600/20",
      iconBg: "bg-indigo-100 dark:bg-indigo-900",
    },
    {
      title: "STATUS",
      value: `${safeStats.totalLeadsStatusUpdated}/${safeStats.totalLeadsInPipeline}`,
      subtitle: 'Updated',
      icon: FileEdit,
      color: "bg-warning/10 text-warning",
      gradient: "from-orange-500/20 to-orange-600/20",
      iconBg: "bg-orange-100 dark:bg-orange-900",
    },
  ];

  if (loading) {
    return (
      <>
        {/* Mobile: Summary card loading */}
        <div className="block md:hidden">
          <Card className="animate-pulse bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4 h-40 bg-muted/20" />
          </Card>
        </div>
        
        {/* Desktop: Grid loading */}
        <div className="hidden md:grid md:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 h-24 bg-muted/50" />
            </Card>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile: Compact summary card like MobileHomeSummary */}
      <div className="block md:hidden">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-900 border-green-200 shadow-sm">
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-2">
                {statCards.map((stat, index) => (
                  <motion.div
                    key={`${stat.title}-${stat.subtitle}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card variant="elevated" className="h-full hover:shadow-md transition-shadow">
                      <CardContent className="p-2.5">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1">
                            <div className={cn("flex h-5 w-5 items-center justify-center rounded-lg", stat.iconBg)}>
                              <stat.icon className="h-3 w-3" style={{ color: stat.color.includes('success') ? '#22c55e' : stat.color.includes('primary') ? '#3b82f6' : stat.color.includes('cyan') ? '#06b6d4' : stat.color.includes('purple') ? '#a855f7' : stat.color.includes('accent') ? '#6366f1' : '#f59e0b' }} />
                            </div>
                            <span className="text-[9px] text-muted-foreground uppercase font-medium truncate">{stat.title}</span>
                          </div>
                          <p className="text-lg font-bold tracking-tight">
                            {stat.value}
                          </p>
                          <p className="text-[9px] text-muted-foreground truncate">{stat.subtitle}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Desktop: Grid layout (original) */}
      <div className="hidden md:grid md:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={`${stat.title}-${stat.subtitle}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <stat.icon className="h-4 w-4" />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.title}</p>
                  <p className="text-xs text-muted-foreground font-medium">{stat.subtitle}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </>
  );
}
